import AttentionRequired from '@copilot/common/pages/campaignDashboard/summaryV2/attentionRequired';
import { UtilityFunctions } from '@copilot/common/utils/common';
import {
    IAttentionLevel,
    ICampaignAttentionType,
    ICampaignSummaryAttentionItemsQuery,
    IOrgMemberAttentionItem,
    IOrgMemberAttentionType,
} from '@copilot/data/graphql/_generated';
import { Typography } from 'antd';
import { ComponentProps, ReactNode } from 'react';
import { AttentionRequiredLevels } from './constant';
import { CAMPAIGN_DETAILS_V2_MENU_ITEMS } from '@copilot/teams/src/pages/campaignDetails/components/campaignDetailsConstants';

/**
 * Map attention items to the issue's copy
 * @param type
 * @param campaignName
 * @returns
 */
function getAttentionItemCopy(type: ICampaignAttentionType, campaignName: string): ReactNode {
    switch (type) {
        case ICampaignAttentionType.CampaignSearchListLow:
            return (
                <Typography.Text>
                    Search list in <Typography.Text strong>{campaignName}</Typography.Text> is
                    running low and a new search list will be needed soon.
                </Typography.Text>
            );
        case ICampaignAttentionType.NoInvitesSetForProspectingCampaign:
            return (
                <Typography.Text>
                    No invites set in <Typography.Text strong>{campaignName}</Typography.Text>. This
                    campaign won’t send new connection requests until invites are added, but
                    follow-up messages will still go to your existing connections.
                </Typography.Text>
            );
        case ICampaignAttentionType.NoMessagesSetForNurtureCampaign:
            return (
                <Typography.Text>
                    No messages set in <Typography.Text strong>{campaignName}</Typography.Text>. The
                    campaign will not run until they are set.
                </Typography.Text>
            );
        default:
            return UtilityFunctions.assertUnreachable(type);
    }
}

/**
 * Map org attention items to the issue's copy
 * @param type
 * @returns
 */
function getOrgAttentionItemCopy(type: IOrgMemberAttentionType): ReactNode {
    switch (type) {
        case IOrgMemberAttentionType.LoggedOutOfLinkedin:
            return <Typography.Text>LinkedIn disconnected</Typography.Text>;
        case IOrgMemberAttentionType.SalesNavMissing:
            return <></>;
        default:
            return UtilityFunctions.assertUnreachable(type);
    }
}

/**
 * Map attention items to onClick event
 * @param type
 * @param campaignId
 * @returns
 */
function getCampaignActionItemOnClick(
    type: ICampaignAttentionType,
    campaignId: string,
    onNavigate: (path: string, state?: unknown) => void
): () => void {
    switch (type) {
        case ICampaignAttentionType.CampaignSearchListLow:
            return () =>
                onNavigate(
                    `/campaign/${campaignId}#${CAMPAIGN_DETAILS_V2_MENU_ITEMS.AUDIENCE}?openAudienceSettings=true`
                );
        case ICampaignAttentionType.NoInvitesSetForProspectingCampaign:
        case ICampaignAttentionType.NoMessagesSetForNurtureCampaign:
            return () =>
                onNavigate(`/campaign/${campaignId}#${CAMPAIGN_DETAILS_V2_MENU_ITEMS.SETTINGS}`);
        default:
            return UtilityFunctions.assertUnreachable(type);
    }
}

/**
 * Map org member attention items to onClick event
 * @returns
 * @param item
 * @param reconnectCallback
 */
function getOrgMemberActionItemOnClick(
    item: IOrgMemberAttentionItem,
    reconnectCallback: (orgMemberId: string) => void
): () => void {
    switch (item.type) {
        case IOrgMemberAttentionType.LoggedOutOfLinkedin:
            return reconnectCallback.bind(undefined, item.memberId);
        case IOrgMemberAttentionType.SalesNavMissing:
            return () => {};
        default:
            return UtilityFunctions.assertUnreachable(item.type);
    }
}

/**
 * Convert item DTO to model
 * @param item
 * @param campaignName
 * @param campaignId
 * @returns
 */
export function toCampaignAttentionRequiredItem(
    item: ArrayElement<ICampaignSummaryAttentionItemsQuery['campaignSummary']['attentionItems']>,
    campaignName: string,
    campaignId: string,
    onNavigate: (path: string, state?: unknown) => void
): ArrayElement<ComponentProps<typeof AttentionRequired>['items']> {
    return {
        issue: getAttentionItemCopy(item.type, campaignName),
        level:
            item.level === IAttentionLevel.Warning
                ? AttentionRequiredLevels.warning
                : AttentionRequiredLevels.blocker,
        actionLabel: 'Update',
        onActionClick: getCampaignActionItemOnClick(item.type, campaignId, onNavigate),
    };
}

/**
 * Convert item DTO to model
 * @param item
 * @param reconnectCallback
 * @returns
 */
export function toOrgMemberAttentionRequiredItem(
    item: ArrayElement<ICampaignSummaryAttentionItemsQuery['orgMembers'][0]['attentionItems']>,
    reconnectCallback: (orgMemberId: string) => void
): ArrayElement<ComponentProps<typeof AttentionRequired>['items']> {
    return {
        issue: getOrgAttentionItemCopy(item.type),
        level:
            item.level === IAttentionLevel.Warning
                ? AttentionRequiredLevels.warning
                : AttentionRequiredLevels.blocker,
        actionLabel: 'Reconnect',
        onActionClick: getOrgMemberActionItemOnClick(item, reconnectCallback),
    };
}
